import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBlockingConfirm } from './useBlockingConfirm';
import { flattenPaperQuestions, APP_MAIN_CONTENT_ID } from '../utils/testUtils';
import { bindInterestResponseKeyboard } from '../utils/interestTestKeyboard';
import {
  filterInterestSessionItems,
  INTEREST_ATTEMPT_SIZE
} from '../utils/interestQuestionSelection';
import {
  clearTestProgress,
  drawInterestTriGroupRound,
  saveTestProgress,
  submitTest
} from '../services/testService';
import {
  INTEREST_SESSION_MODEL_TRI_GROUP,
  INTEREST_TRI_GROUP_ROUND_COUNT,
  INTEREST_TRI_GROUP_ROUND_SIZE
} from '../config/interestSessionConfig';

const SWIPE_THRESHOLD = 80;
const PREVIEW_MIN_DELTA = 28;
const COMMIT_FLASH_MS = 260;
const SWIPE_SUPPRESS_SELECT_CLASS = 'interest-swipe-suppress-select';
const TRI_GROUP_DRAW_METHOD = 'tri_group_v1';

function hasToken() {
  return Boolean(localStorage.getItem('token'));
}

function getSwipePreview(dx, dy) {
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  if (Math.max(absDx, absDy) < PREVIEW_MIN_DELTA) return null;
  if (absDx > absDy) {
    if (dx >= PREVIEW_MIN_DELTA) return 1;
    if (dx <= -PREVIEW_MIN_DELTA) return -1;
    return null;
  }
  if (dy >= PREVIEW_MIN_DELTA) return 0;
  return null;
}

export function useInterestTriGroupSession({ paper, savedProgress = null, onProgressReload }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { confirmAsync, isConfirmOpen, ConfirmDialog } = useBlockingConfirm();
  const lang = i18n.language || 'en';

  const [phase, setPhase] = useState('intro');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [clusterScores, setClusterScores] = useState(null);
  const [committedFlash, setCommittedFlash] = useState(null);
  const commitTimerRef = useRef(null);
  const interestExitRef = useRef({
    phase: 'intro',
    resetSession: () => {},
    navigate: () => {},
    pauseToIntro: async () => {}
  });
  const [dragState, setDragState] = useState({
    active: false,
    startX: 0,
    startY: 0,
    dx: 0,
    dy: 0
  });
  const [saveStatus, setSaveStatus] = useState('idle');
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [isBeginningSession, setIsBeginningSession] = useState(false);
  const [currentRound, setCurrentRound] = useState(1);
  const [completedRounds, setCompletedRounds] = useState([]);
  const [nextRound, setNextRound] = useState(1);
  const [allRoundsComplete, setAllRoundsComplete] = useState(false);
  const [cumulativeQuestionIds, setCumulativeQuestionIds] = useState([]);
  const [scoringCount, setScoringCount] = useState(0);

  const allItems = useMemo(() => flattenPaperQuestions(paper), [paper]);
  const items = useMemo(
    () => filterInterestSessionItems(allItems, selectedQuestionIds),
    [allItems, selectedQuestionIds]
  );
  const total = items.length;
  const currentItem = items[questionIndex];

  const canResume = useMemo(() => {
    if (!savedProgress || savedProgress.resumePhase !== 'questions') return false;
    const ids = Array.isArray(savedProgress.selectedQuestionIds)
      ? savedProgress.selectedQuestionIds
      : [];
    if (ids.length !== INTEREST_TRI_GROUP_ROUND_SIZE) return false;
    const answersObj =
      savedProgress.answers && typeof savedProgress.answers === 'object'
        ? savedProgress.answers
        : {};
    const qi = typeof savedProgress.questionIndex === 'number' ? savedProgress.questionIndex : 0;
    if (qi <= 0 && Object.keys(answersObj).length === 0) return false;
    return qi <= Math.max(0, INTEREST_TRI_GROUP_ROUND_SIZE - 1);
  }, [savedProgress]);

  const canContinueNextRound = useMemo(
    () =>
      !allRoundsComplete &&
      completedRounds.length > 0 &&
      nextRound != null &&
      (phase === 'complete' || phase === 'intro'),
    [allRoundsComplete, completedRounds.length, phase, nextRound]
  );

  const answersRef = useRef(answers);
  const questionIndexRef = useRef(questionIndex);
  const selectedQuestionIdsRef = useRef(selectedQuestionIds);
  const cumulativeQuestionIdsRef = useRef(cumulativeQuestionIds);
  const currentRoundRef = useRef(currentRound);
  const completedRoundsRef = useRef(completedRounds);
  const nextRoundRef = useRef(nextRound);
  const drawVersionRef = useRef(null);

  const interestProgressFields = () => ({
    selectedQuestionIds: selectedQuestionIdsRef.current,
    drawMethod: TRI_GROUP_DRAW_METHOD,
    sessionModel: INTEREST_SESSION_MODEL_TRI_GROUP,
    interestRound: currentRoundRef.current,
    interestCompletedRounds: completedRoundsRef.current,
    ...(drawVersionRef.current ? { drawVersion: drawVersionRef.current } : {})
  });

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  useEffect(() => {
    questionIndexRef.current = questionIndex;
  }, [questionIndex]);
  useEffect(() => {
    selectedQuestionIdsRef.current = selectedQuestionIds;
  }, [selectedQuestionIds]);
  useEffect(() => {
    cumulativeQuestionIdsRef.current = cumulativeQuestionIds;
  }, [cumulativeQuestionIds]);
  useEffect(() => {
    currentRoundRef.current = currentRound;
  }, [currentRound]);
  useEffect(() => {
    completedRoundsRef.current = completedRounds;
  }, [completedRounds]);
  useEffect(() => {
    nextRoundRef.current = nextRound;
  }, [nextRound]);

  useEffect(
    () => () => {
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    },
    []
  );

  useEffect(() => {
    const main = document.getElementById(APP_MAIN_CONTENT_ID);
    if (!main) return undefined;
    if (phase === 'swiping') main.classList.add(SWIPE_SUPPRESS_SELECT_CLASS);
    else main.classList.remove(SWIPE_SUPPRESS_SELECT_CLASS);
    return () => main.classList.remove(SWIPE_SUPPRESS_SELECT_CLASS);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'swiping' || !hasToken()) return undefined;
    const slug = paper.slug;
    const id = window.setTimeout(() => {
      saveTestProgress(slug, {
        paperType: 'interest',
        answers: answersRef.current,
        questionIndex: questionIndexRef.current,
        remainingSeconds: 0,
        resumePhase: 'questions',
        ...interestProgressFields()
      }).catch(console.error);
    }, 2000);
    return () => clearTimeout(id);
  }, [answers, questionIndex, phase, paper.slug, currentRound, completedRounds]);

  const progressPercent = Math.round((questionIndex / Math.max(1, total)) * 100);
  const isComplete = questionIndex >= total;
  const interactionLocked = committedFlash !== null;

  const swipePreview = useMemo(() => {
    if (committedFlash !== null) return committedFlash;
    if (!dragState.active) return null;
    return getSwipePreview(dragState.dx, dragState.dy);
  }, [committedFlash, dragState.active, dragState.dx, dragState.dy]);

  const submitSession = async (finalAnswers) => {
    setIsSubmitting(true);
    setSubmitError('');
    setClusterScores(null);
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const scoringQuestionIds = [
          ...cumulativeQuestionIdsRef.current,
          ...selectedQuestionIdsRef.current
        ];
        const result = await submitTest({
          paperSlug: paper.slug,
          paperType: 'interest',
          answers: finalAnswers,
          completionReason: 'submitted',
          selectedQuestionIds: selectedQuestionIdsRef.current,
          scoringQuestionIds,
          drawMethod: TRI_GROUP_DRAW_METHOD,
          drawVersion: drawVersionRef.current,
          interestRound: currentRoundRef.current
        });
        const tri = result?.triGroup;
        if (tri) {
          setCompletedRounds(tri.completedRounds || []);
          completedRoundsRef.current = tri.completedRounds || [];
          setNextRound(tri.nextRound ?? null);
          nextRoundRef.current = tri.nextRound ?? null;
          setAllRoundsComplete(Boolean(tri.allRoundsComplete));
          setScoringCount(tri.scoringCount || scoringQuestionIds.length);
          if (tri.nextRound != null) {
            setCurrentRound(tri.nextRound);
            currentRoundRef.current = tri.nextRound;
          }
        } else if (currentRoundRef.current < INTEREST_TRI_GROUP_ROUND_COUNT) {
          const done = currentRoundRef.current;
          const completed = Array.from({ length: done }, (_, i) => i + 1);
          setCompletedRounds(completed);
          completedRoundsRef.current = completed;
          const upcoming = done + 1;
          setNextRound(upcoming);
          nextRoundRef.current = upcoming;
          setCurrentRound(upcoming);
          currentRoundRef.current = upcoming;
          setScoringCount(scoringQuestionIds.length);
        }
        if (Array.isArray(result?.cluster_scores) && result.cluster_scores.length > 0) {
          setClusterScores(result.cluster_scores);
        }
      }
      const completedCount = completedRoundsRef.current.length;
      const moreRoundsRemain =
        completedCount > 0 && completedCount < INTEREST_TRI_GROUP_ROUND_COUNT;
      setPhase('complete');
      // Stay at index 0 between rounds so "Continue" does not inherit index === total (blank swiping UI).
      if (moreRoundsRemain) {
        setQuestionIndex(0);
        questionIndexRef.current = 0;
      } else {
        setQuestionIndex(total);
      }
    } catch (error) {
      console.error(error);
      setSubmitError(t('interestTest.submitError'));
      setPhase('complete');
      const retryRound = Math.min(
        INTEREST_TRI_GROUP_ROUND_COUNT,
        Math.max(1, completedRoundsRef.current.length + 1)
      );
      setNextRound(retryRound);
      nextRoundRef.current = retryRound;
      setCurrentRound(retryRound);
      currentRoundRef.current = retryRound;
      setQuestionIndex(0);
      questionIndexRef.current = 0;
    } finally {
      setIsSubmitting(false);
      onProgressReload?.('submitted');
    }
  };

  const applyAnswer = (value) => {
    if (!currentItem) return;
    const questionId = currentItem.question.id;
    setAnswers((prev) => {
      const nextAnswers = { ...prev, [questionId]: value };
      const nextIndex = questionIndex + 1;
      queueMicrotask(() => {
        if (nextIndex >= total) submitSession(nextAnswers);
        else setQuestionIndex(nextIndex);
      });
      return nextAnswers;
    });
  };

  const commitResponse = (value) => {
    if (!currentItem || interactionLocked || isSubmitting) return;
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    setCommittedFlash(value);
    commitTimerRef.current = window.setTimeout(() => {
      commitTimerRef.current = null;
      setCommittedFlash(null);
      applyAnswer(value);
    }, COMMIT_FLASH_MS);
  };

  const commitResponseRef = useRef(commitResponse);
  commitResponseRef.current = commitResponse;
  const interactionLockedRef = useRef(interactionLocked);
  interactionLockedRef.current = interactionLocked;

  useEffect(() => {
    if (phase !== 'swiping' || isComplete || !currentItem || isSubmitting) {
      return undefined;
    }
    return bindInterestResponseKeyboard(
      (value) => {
        if (interactionLockedRef.current) return;
        commitResponseRef.current(value);
      },
      { isConfirmOpen }
    );
  }, [phase, isComplete, currentItem, isSubmitting, isConfirmOpen]);

  const pauseToIntro = async () => {
    setDragState({ active: false, startX: 0, startY: 0, dx: 0, dy: 0 });
    setCommittedFlash(null);
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    if (hasToken()) {
      try {
        setSaveStatus('saving');
        await saveTestProgress(paper.slug, {
          paperType: 'interest',
          answers: answersRef.current,
          questionIndex: questionIndexRef.current,
          remainingSeconds: 0,
          resumePhase: 'questions',
          ...interestProgressFields()
        });
        setSaveStatus('saved');
      } catch (e) {
        console.error(e);
        setSaveStatus('failed');
      }
      onProgressReload?.();
    }
    setPhase('intro');
    setQuestionIndex(0);
    setAnswers({});
    setSubmitError('');
  };

  const resetSession = () => {
    if (hasToken()) clearTestProgress(paper.slug, 'interest').catch(() => {});
    onProgressReload?.();
    setPhase('intro');
    setQuestionIndex(0);
    setAnswers({});
    setSelectedQuestionIds([]);
    setCurrentRound(1);
    setCompletedRounds([]);
    setNextRound(1);
    nextRoundRef.current = 1;
    setAllRoundsComplete(false);
    setCumulativeQuestionIds([]);
    setScoringCount(0);
    drawVersionRef.current = null;
    setSubmitError('');
    setClusterScores(null);
    setCommittedFlash(null);
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isConfirmOpen()) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (target.closest('nav') || target.closest('footer')) return;
      const inMain = target.closest(`#${APP_MAIN_CONTENT_ID}`);
      if (!inMain && target !== document.body && target !== document.documentElement) return;
      e.preventDefault();
      e.stopPropagation();
      setDragState({ active: false, startX: 0, startY: 0, dx: 0, dy: 0 });
      void (async () => {
        const { phase: ph, resetSession: rs, navigate: nav, pauseToIntro: pti } =
          interestExitRef.current;
        if (ph === 'intro') {
          if (!(await confirmAsync(t('interestTest.confirmExitToHome')))) return;
          nav('/');
          return;
        }
        if (ph === 'swiping') {
          if (!(await confirmAsync(t('interestTest.confirmPauseToIntro')))) return;
          await pti();
          return;
        }
        if (ph === 'complete') {
          if (!(await confirmAsync(t('interestTest.confirmExitToIntro')))) return;
          rs();
        }
      })();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [t, confirmAsync, isConfirmOpen]);

  const applyDrawState = (draw, { preserveAnswers = false } = {}) => {
    const ids = draw.selectedQuestionIds || [];
    setSelectedQuestionIds(ids);
    selectedQuestionIdsRef.current = ids;
    setCurrentRound(draw.round || 1);
    currentRoundRef.current = draw.round || 1;
    setCompletedRounds(draw.completedRounds || []);
    completedRoundsRef.current = draw.completedRounds || [];
    setNextRound(draw.nextRound ?? draw.round ?? 1);
    nextRoundRef.current = draw.nextRound ?? draw.round ?? 1;
    setAllRoundsComplete(Boolean(draw.allRoundsComplete));
    setCumulativeQuestionIds(draw.cumulativeQuestionIds || []);
    cumulativeQuestionIdsRef.current = draw.cumulativeQuestionIds || [];
    drawVersionRef.current = draw.drawVersion || null;
    if (!preserveAnswers) {
      setAnswers({});
    }
    // Always restart at the first question of the new round (round 1 ends with index === total).
    setQuestionIndex(0);
    questionIndexRef.current = 0;
    setClusterScores(null);
    setCommittedFlash(null);
    setSaveStatus('idle');
    setDragState({ active: false, startX: 0, startY: 0, dx: 0, dy: 0 });
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    setPhase('swiping');
  };

  const beginRound = async ({ round, completed, preserveAnswers = false, priorAnswers = {} } = {}) => {
    setIsBeginningSession(true);
    setSubmitError('');
    try {
      let draw;
      if (hasToken()) {
        draw = await drawInterestTriGroupRound({ round, completedRounds: completed });
      } else {
        throw new Error(t('interestTest.triGroupLoginRequired'));
      }
      if (preserveAnswers) setAnswers(priorAnswers);
      applyDrawState(draw, { preserveAnswers });
    } catch (e) {
      setSubmitError(e.message || 'Failed to start interest test round');
    } finally {
      setIsBeginningSession(false);
    }
  };

  const handleBeginSessionClick = async () => {
    if (canResume) {
      if (!(await confirmAsync(t('interestTest.confirmDiscardSavedProgress')))) return;
      if (hasToken()) {
        try {
          await clearTestProgress(paper.slug, 'interest');
        } catch {
          // ignore
        }
        onProgressReload?.();
      }
    }
    await beginRound({ round: 1, completed: [] });
  };

  const handleContinueSession = async () => {
    if (!savedProgress || !canResume) return;
    const ids = Array.isArray(savedProgress.selectedQuestionIds)
      ? [...savedProgress.selectedQuestionIds]
      : [];
    const answersObj =
      savedProgress.answers && typeof savedProgress.answers === 'object'
        ? { ...savedProgress.answers }
        : {};
    const qi = Math.min(
      Math.max(0, savedProgress.questionIndex || 0),
      INTEREST_TRI_GROUP_ROUND_SIZE - 1
    );
    const round = savedProgress.interestRound || 1;
    const completed = Array.isArray(savedProgress.interestCompletedRounds)
      ? [...savedProgress.interestCompletedRounds]
      : [];
    setSelectedQuestionIds(ids);
    selectedQuestionIdsRef.current = ids;
    setCurrentRound(round);
    currentRoundRef.current = round;
    setCompletedRounds(completed);
    completedRoundsRef.current = completed;
    drawVersionRef.current =
      typeof savedProgress.drawVersion === 'string' ? savedProgress.drawVersion : null;
    setAnswers(answersObj);
    answersRef.current = answersObj;
    setQuestionIndex(qi);
    questionIndexRef.current = qi;
    setSubmitError('');

    // Round 2/3 need prior-round ids for cumulative scoring on submit.
    if (completed.length > 0 && hasToken()) {
      try {
        const draw = await drawInterestTriGroupRound({ round, completedRounds: completed });
        const cumulative = draw.cumulativeQuestionIds || [];
        setCumulativeQuestionIds(cumulative);
        cumulativeQuestionIdsRef.current = cumulative;
        if (typeof draw.drawVersion === 'string') {
          drawVersionRef.current = draw.drawVersion;
        }
      } catch (e) {
        console.error(e);
        setSubmitError(e.message || 'Failed to restore session');
        setPhase('intro');
        return;
      }
    }

    setPhase('swiping');
  };

  const handleContinueNextRound = async () => {
    const roundToStart =
      nextRoundRef.current ??
      nextRound ??
      Math.min(INTEREST_TRI_GROUP_ROUND_COUNT, completedRoundsRef.current.length + 1);
    if (!roundToStart) return;
    const priorAnswers = { ...answersRef.current };
    await beginRound({
      round: roundToStart,
      completed: completedRoundsRef.current,
      preserveAnswers: true,
      priorAnswers
    });
  };

  const handleRestartClick = () => {
    void (async () => {
      if (!(await confirmAsync(t('interestTest.confirmRestart')))) return;
      resetSession();
    })();
  };

  const handleTryAgainClick = () => {
    void (async () => {
      if (allRoundsComplete) {
        if (!(await confirmAsync(t('interestTest.confirmStartOver')))) return;
        resetSession();
        return;
      }
      if (canContinueNextRound) {
        setPhase('intro');
        setQuestionIndex(0);
        setSelectedQuestionIds([]);
        selectedQuestionIdsRef.current = [];
        setSubmitError('');
        return;
      }
      if (!(await confirmAsync(t('interestTest.confirmRestart')))) return;
      resetSession();
    })();
  };

  interestExitRef.current = { phase, resetSession, navigate, pauseToIntro };

  useEffect(() => {
    if (!savedProgress) return;
    const completed = Array.isArray(savedProgress.interestCompletedRounds)
      ? savedProgress.interestCompletedRounds
      : [];
    if (!completed.length) return;
    setCompletedRounds(completed);
    completedRoundsRef.current = completed;
    const next = savedProgress.interestRound || completed.length + 1;
    setCurrentRound(next);
    currentRoundRef.current = next;
    setNextRound(next);
    nextRoundRef.current = next;
    setAllRoundsComplete(completed.length >= INTEREST_TRI_GROUP_ROUND_COUNT);
    if (savedProgress.answers && typeof savedProgress.answers === 'object') {
      setAnswers({ ...savedProgress.answers });
    }
    if (savedProgress.resumePhase === 'cover') {
      setQuestionIndex(0);
      questionIndexRef.current = 0;
    }
    drawVersionRef.current =
      typeof savedProgress.drawVersion === 'string' ? savedProgress.drawVersion : null;
  }, [savedProgress]);

  const beginDrag = (event) => {
    if (phase !== 'swiping' || !currentItem || isSubmitting || interactionLocked) return;
    const point = event.touches?.[0] || event;
    setDragState({ active: true, startX: point.clientX, startY: point.clientY, dx: 0, dy: 0 });
  };

  const updateDrag = (event) => {
    if (!dragState.active) return;
    const point = event.touches?.[0] || event;
    setDragState((prev) => ({
      ...prev,
      dx: point.clientX - prev.startX,
      dy: point.clientY - prev.startY
    }));
  };

  const endDrag = () => {
    if (!dragState.active) return;
    const { dx, dy } = dragState;
    setDragState({ active: false, startX: 0, startY: 0, dx: 0, dy: 0 });
    let value = null;
    if (dy > SWIPE_THRESHOLD && Math.abs(dy) > Math.abs(dx)) value = 0;
    else if (dx > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) value = 1;
    else if (dx < -SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) value = -1;
    if (value !== null) commitResponse(value);
  };

  const cardStyle =
    phase === 'swiping' && dragState.active && !interactionLocked
      ? {
          transform: `translate(${dragState.dx}px, ${dragState.dy}px) rotate(${dragState.dx * 0.04}deg)`,
          transition: 'none'
        }
      : {};

  const cardBorderClass =
    swipePreview === 1
      ? 'border-2 border-emerald-400 shadow-emerald-100/80 ring-2 ring-emerald-200/90 dark:shadow-emerald-900/40 dark:ring-emerald-700/80'
      : swipePreview === 0
        ? 'border-2 border-sky-400 shadow-sky-100/80 ring-2 ring-sky-200/90 dark:shadow-sky-900/40 dark:ring-sky-700/80'
        : swipePreview === -1
          ? 'border-2 border-rose-400 shadow-rose-100/80 ring-2 ring-rose-200/90 dark:shadow-rose-900/40 dark:ring-rose-700/80'
          : 'border border-edge';

  const optionButtonClass = (value) => {
    const base =
      value === -1
        ? 'bg-rose-50 text-rose-700 border border-rose-100 hover:bg-rose-100 dark:bg-rose-950/50 dark:text-rose-200 dark:border-rose-800/60 dark:hover:bg-rose-900/50'
        : value === 0
          ? 'bg-sky-50 text-sky-700 border border-sky-100 hover:bg-sky-100 dark:bg-sky-950/50 dark:text-sky-200 dark:border-sky-800/60 dark:hover:bg-sky-900/50'
          : 'bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-800/60 dark:hover:bg-emerald-900/50';
    const accentHighlight =
      value === -1
        ? 'ring-2 ring-rose-400 ring-offset-2 ring-offset-canvas shadow-md shadow-rose-300/40 dark:shadow-rose-900/40'
        : value === 0
          ? 'ring-2 ring-sky-400 ring-offset-2 ring-offset-canvas shadow-md shadow-sky-300/40 dark:shadow-sky-900/40'
          : 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-canvas shadow-md shadow-emerald-300/40 dark:shadow-emerald-900/40';
    const selected =
      swipePreview === value ? ` font-semibold scale-[1.02] ${accentHighlight}` : '';
    return `px-4 py-3 rounded-lg transition-[box-shadow,transform] duration-150 disabled:opacity-55 disabled:cursor-not-allowed ${base}${selected}`;
  };

  return {
    ConfirmDialog,
    t,
    lang,
    phase,
    canResume,
    handleContinueSession,
    handleBeginSessionClick,
    isBeginningSession,
    currentItem,
    isComplete,
    questionIndex,
    total,
    handleRestartClick,
    progressPercent,
    beginDrag,
    updateDrag,
    endDrag,
    interactionLocked,
    cardBorderClass,
    cardStyle,
    commitResponse,
    optionButtonClass,
    submitError,
    handleTryAgainClick,
    saveStatus,
    clusterScores,
    sessionModel: INTEREST_SESSION_MODEL_TRI_GROUP,
    currentRound,
    totalRounds: INTEREST_TRI_GROUP_ROUND_COUNT,
    completedRounds,
    canContinueNextRound,
    nextRound,
    handleContinueNextRound,
    allRoundsComplete,
    scoringCount
  };
}
